import { Box, Typography, Paper, useTheme, FormGroup, FormControlLabel, Checkbox } from "@mui/material";
import { useState, useMemo } from "react";
import DataGridMAIQ from "app/components/ReusableUIComponents/HighLevelComponents/DataGridMAIQ";
import DataGridTypes from "types/DataGridTypes";
import AddResourceModal from "./AddResourceModal";
import ResourceDetailsModal from "./ResourceDetailsModal";
import { Resource } from "../../types";
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import ButtonMAIQ from "app/components/ReusableUIComponents/LowLevelComponents/buttons/ButtonMAIQ";
import FilterListIcon from '@mui/icons-material/FilterList';

const ResourcesTab = () => {
  const [isAddResourceModal, setIsAddResourceModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
  const [showPages, setShowPages] = useState(true);
  const [showComponents, setShowComponents] = useState(true);
  const theme = useTheme();

  const typeFilter = useMemo(() => {
    if (showPages && showComponents) return undefined;
    if (showComponents) return 'COMPONENT';
    return 'PAGE';
  }, [showPages, showComponents]);

  const onlyPagesSelected = showPages && !showComponents;
  const onlyComponentsSelected = showComponents && !showPages;

  const gridType = useMemo(() => DataGridTypes.RESOURCES(typeFilter), [typeFilter]);

  const handleAddResourceModalOpen = () => {
    setIsAddResourceModal(true);
  };

  const handleResourceClick = (resource: any) => {
    setSelectedResource(resource.row);
  };

  const handleCloseResourceDetails = () => {
    setSelectedResource(null);
  };

  return (
    <Box 
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        height: "100%",
        p: 0,
      }}
    >
      <Paper 
        elevation={0}
        sx={{ 
          p: 1.5,
          display: "flex",
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: "space-between",
          alignItems: { xs: 'flex-start', sm: 'center' },
          borderRadius: 1.5,
          backgroundColor: 'background.paper',
          boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)',
          gap: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <SettingsIcon 
            sx={{ 
              color: theme.palette.primary.main,
              fontSize: 28 
            }} 
          />
          <Box>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600, 
                fontSize: '1.1rem',
                color: theme.palette.primary.main
              }}
            >
              Resource Management
            </Typography>
            <Typography 
              variant="body2" 
              color="text.primary"
              sx={{ fontSize: '0.85rem' }}
            >
              Create and manage system resources
            </Typography>
          </Box>
        </Box>
        
        <ButtonMAIQ
          variant="contained"
          color="primary"
          onClick={handleAddResourceModalOpen}
          startIcon={<AddIcon />}
          sx={{
            borderRadius: 1,
            fontWeight: 600,
            px: 2.5,
            py: 1,
          }}
        >
          Add New Resource
        </ButtonMAIQ>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          px: 1,
          py: 0.75,
          borderRadius: 1.5,
          backgroundColor: 'background.paper',
          boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <FilterListIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
          <Typography variant="subtitle2" fontWeight={600} color="text.primary">
            Show:
          </Typography>
        </Box>
        <FormGroup row sx={{ gap: 0.5 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={showPages}
                onChange={(_, checked) => setShowPages(checked)}
                size="small"
                color="primary"
                disabled={onlyPagesSelected}
              />
            }
            label="Pages"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={showComponents}
                onChange={(_, checked) => setShowComponents(checked)}
                size="small"
                color="primary"
                disabled={onlyComponentsSelected}
              />
            }
            label="Components"
          />
        </FormGroup>
      </Paper>

      <Box 
        sx={{ 
          flexGrow: 1, 
          minHeight: 400,
          '& .MuiDataGrid-root': {
            border: 'none',
            borderRadius: 1.5,
            boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)',
          },
        }}
      >
        <DataGridMAIQ 
          key={`${refreshKey}-${typeFilter ?? 'all'}`}
          type={gridType}
          onRowClick={handleResourceClick}
        />
      </Box>

      <AddResourceModal
        open={isAddResourceModal}
        onClose={() => setIsAddResourceModal(false)}
        onSuccess={() => setRefreshKey(prev => prev + 1)}
      />
      {selectedResource && (
        <ResourceDetailsModal
          open={!!selectedResource}
          onClose={handleCloseResourceDetails}
          resource={selectedResource}
          onSuccess={() => {
            setRefreshKey(prev => prev + 1);
            handleCloseResourceDetails();
          }}
        />
      )}
    </Box>
  );
};

export default ResourcesTab; 